import { useEffect, useState } from "react";
import {
  ErrorModal,
  LoadingIndicator,
  Typography,
  tokens,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import {
  DIRECT_TO_HOME,
  FUSION_CLOSER,
  FUSION_SETTER,
  getSplatUserType,
} from "@sunrun/sales-experience-shared";
// This is an exception as this "page" renders pages.
// eslint-disable-next-line import/no-restricted-paths
import { PricingPage } from "components/pages/PricingPage";
import {
  HeaderButton,
  HeaderContentLayout,
} from "components/templates/HeaderContentLayout";
import { useAppSelector } from "store";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { amendOfferWithSync, getEditOffer } from "services/storeFront";
import { useCapContext } from "cap/providers/CapContext";
import { useNavigateHelper } from "utils/useNavigateHelper";
import { ChangeOrderDelayModal } from "components/organisms/ChangeOrderDelayModal";
import { ChangeOrderDesignRender } from "components/organisms/ChangeOrderDesignRender";
import { getReworkTaskByName, getReworkTaskDetails } from "cap/api/capApi";
import { RepricingLoadingWizard } from "cap/components/organisms/RepricingLoadingWizard";

const RepChangeOrderPricingPage = () => {
  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const [userType, setUserType] = useState<string>();
  const navigate = useNavigateHelper({ replace: true });
  const [showReprepricingWizard, setShowReprepricingWizard] = useState(false);
  const [lightmileProjectId, setLightmileProjectId] = useState<string>();
  const [designId, setDesignId] = useState<string>(); // if designId is present we know that design iframe has added lightmile project id to offer
  const [taskId, setTaskId] = useState<string>();
  const [taskEligible, setTaskEligible] = useState<boolean>(false);
  const [hudEligibilityError, setHudEligibilityError] = useState<Error | null>(
    null
  );
  const [reviewOfferId, setReviewOfferId] = useState<string>();
  const [selectedPricePointId, setSelectedPricePointId] = useState<string>();
  const [submitOfferSuccess, setSubmitOfferSuccess] = useState(false);
  const [submitOfferFailure, setSubmitOfferFailure] = useState<Error | null>(
    null
  );
  const [determinantTaskId, setDeterminantTaskId] = useState<string>();
  const [initialTaskName, setInitialTaskName] = useState<string>();
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [ihdError, setIhdError] = useState<Error | null>(null);
  const [warning, setWarning] = useState<Error | null>(null);
  const { activeProposal, prospectId, sow, userProfile, originalProposal } =
    useCapContext();
  const { proposal: sowProposal, ultimateParentProposal } = sow || {};

  useEffect(() => {
    if (userProfile) {
      setUserType(getSplatUserType(userProfile));
    }
  }, [userProfile]);

  useEffect(() => {
    if (sow && !activeProposal) {
      setError(new Error("No active proposal present"));
      return;
    }
  }, [activeProposal, sow]);

  useEffect(() => {
    if (!prospectId || !cognitoToken || !activeProposal || !sowProposal) {
      return;
    }
    const signedRootProposalId = ultimateParentProposal?.signed
      ? ultimateParentProposal.externalId
      : originalProposal?.proposalId;

    amendOfferWithSync(
      cognitoToken,
      prospectId,
      activeProposal.proposalId,
      signedRootProposalId || activeProposal.proposalId // Default to active proposal if no signed root proposal is present
    )
      .then((amendData) => {
        setReviewOfferId(amendData);
      })
      .catch((err) => {
        setError(err);
      });
  }, [
    cognitoToken,
    prospectId,
    activeProposal,
    sowProposal,
    ultimateParentProposal,
    originalProposal,
  ]);

  useEffect(() => {
    if (!designId || !reviewOfferId || !cognitoToken) {
      return;
    }
    getEditOffer(cognitoToken, reviewOfferId)
      .then((data) => {
        setLightmileProjectId(data.lightmileProjectId);
      })
      .catch((err) => {
        setError(err);
      });
  }, [reviewOfferId, designId, cognitoToken]);

  useEffect(() => {
    if (!authKey || !prospectId || !userType || !sow) {
      return;
    }
    const { priceToCustomer } = sow;

    let taskName = "Prepare CAP Package";
    if (
      (userType === DIRECT_TO_HOME ||
        userType === FUSION_CLOSER ||
        userType === FUSION_SETTER) &&
      priceToCustomer > 0
    ) {
      taskName = "Home Upgrades Deduction";
    }
    setInitialTaskName(taskName);
    getReworkTaskByName(authKey, prospectId, taskName)
      .then((data) => {
        const task = data[0];

        if (!task?.taskId) {
          setError(new Error("No task id is present on this task."));
          return;
        }
        setTaskId(task.taskId);
        getReworkTaskDetails(authKey, task.taskId)
          .then((taskDetails) => {
            const { ReworkTaskDetails } = taskDetails;
            const {
              reworkEligible,
              reworkIneligibleReason,
              determinantTaskList,
            } = ReworkTaskDetails;

            if (determinantTaskList) {
              const determinantTaskKey = Object.keys(determinantTaskList)[0];
              setDeterminantTaskId(determinantTaskKey);
            }

            if (reworkEligible) {
              setTaskEligible(true);
            } else if (taskName === "Home Upgrades Deduction") {
              setHudEligibilityError(new Error(reworkIneligibleReason));
            } else {
              setError(
                new Error(
                  `The task '${taskName}' is ineligible for rework: ${reworkIneligibleReason}`
                )
              );
            }
          })
          .catch((err) => {
            setError(err);
          });
      })
      .catch((err) => {
        setError(err);
      });
  }, [authKey, prospectId, sow, userType]);

  const hudEligibilityPrimaryButton = {
    text: "Acknowledge",
    onClick: () => setHudEligibilityError(null),
    testId:
      "rep-change-order-pricing-hud-eligibility-error-modal-acknowledge-button",
    ariaLabel:
      "rep-change-order-pricing-hud-eligibility-error-modal-acknowledge-button",
    disabled: false,
  };

  if (ihdError) {
    return (
      <ErrorModal
        testId="rep-change-order-pricing-ihd-error-modal"
        title="Error from another system"
        error={ihdError ?? undefined}
        onClose={() => {
          setIhdError(null);
        }}
      >
        <Typography color={tokens.TINTS_OFF_WHITE_20}>
          ONE received an error from another system. This is not an issue from
          ONE but from another technical system that ONE relies on.
        </Typography>
        <ErrorComponent
          context="RepChangeOrderPricingPage"
          action="IHD Error"
          error={ihdError ?? undefined}
          errorFromAnotherSystem
        />
      </ErrorModal>
    );
  }
  if (error) {
    return (
      <ErrorModal
        error={error ?? undefined}
        testId="rep-change-order-pricing-loading-error-modal"
        onClose={() => {
          setError(null);
        }}
      >
        <ErrorComponent
          context="RepChangeOrderPricingPage"
          action="Loading Error"
          error={error ?? undefined}
        />
      </ErrorModal>
    );
  }
  if (hudEligibilityError) {
    return (
      <ErrorModal
        testId="rep-change-order-pricing-hud-eligibility-error-modal"
        title="You may need to call SSD to reprice this CAP"
        onClose={() => setHudEligibilityError(null)}
        error={hudEligibilityError ?? undefined}
        primaryButton={hudEligibilityPrimaryButton}
      >
        <Grid>
          <Typography>
            The Home Upgrade Deduction task of the WFM project is set to
            inactive, you may not be able to reprice this CAP. If you are able
            to reprice, you may still need to call SSD in order to rework the
            Home Upgrades Deduction task.
          </Typography>
        </Grid>
      </ErrorModal>
    );
  }
  if (warning) {
    return <ChangeOrderDelayModal setWarning={setWarning} />;
  }

  const eligibleToFinish =
    initialTaskName === "Home Upgrades Deduction" || taskEligible;

  const ChangeOrderButtons: HeaderButton[] = [
    {
      type: "secondary",
      text: "Back",
      onClick: () => {
        navigate(`/cap`);
      },
    },
    {
      type: "primary",
      text: "Finish",
      disabled:
        !selectedPricePointId ||
        submittingOffer ||
        !lightmileProjectId ||
        !eligibleToFinish,
      onClick: () => {
        setSubmittingOffer(true);
        setShowReprepricingWizard(true);
      },
    },
  ];

  const loadingPricingPage = !lightmileProjectId || !reviewOfferId;

  return (
    <>
      {!!reviewOfferId && (
        <ChangeOrderDesignRender
          offerId={reviewOfferId}
          setDesignId={setDesignId}
          setError={setIhdError}
        />
      )}
      {showReprepricingWizard &&
        taskId &&
        initialTaskName &&
        activeProposal?.proposalId && (
          <RepricingLoadingWizard
            submittingOffer={submittingOffer}
            submitOfferSuccess={submitOfferSuccess}
            submitOfferFailure={submitOfferFailure}
            userId={userProfile?.userId as string}
            determinantTaskId={determinantTaskId}
            initialTaskName={initialTaskName}
            initialTaskId={taskId}
            taskEligible={taskEligible}
          />
        )}
      <HeaderContentLayout
        smallPadding
        description={
          <Typography>
            Select the product and price for your customer’s new CAP proposal
            then click Finish. Follow all on-screen instructions after clicking
            Finish.
          </Typography>
        }
        content={
          loadingPricingPage ? (
            <div>
              <LoadingIndicator color="black" message="Getting pricing..." />
            </div>
          ) : (
            <PricingPage
              lightmileProjectId={lightmileProjectId}
              repChangeOrder
              offerId={reviewOfferId as string}
              prospectId={prospectId!}
              changeOrder={true}
              offerEvSpan={true}
              onPricePointIdSelected={(pricePointId) => {
                setSelectedPricePointId(pricePointId);
              }}
              onSubmitFailure={(err: Error) => {
                setSubmittingOffer(false);
                setSubmitOfferFailure(err);
              }}
              onSubmitComplete={() => {
                setSubmitOfferSuccess(true);
                setSubmittingOffer(false);
              }}
              submittingOffer={submittingOffer}
              setChangeOrderError={setWarning}
            />
          )
        }
        title="Pricing"
        buttons={ChangeOrderButtons}
      />
    </>
  );
};

const Grid = styled.div`
  display: grid;
  gap: 20px;
`;

export { RepChangeOrderPricingPage };
